import { useMemo } from "react";
import { UseQueryResult, UseMutationResult } from "@tanstack/react-query";
import { UserLoadingState, UserLoadingReason } from "../context/UsersContext";
import { ReadUser, UpdateUser, CreateUser } from "@/types/user";
import { ApiError } from "@/services/api";

export interface MutationsObject {
  activateUserMutation: UseMutationResult<
    unknown,
    ApiError,
    undefined,
    unknown
  >;
  deactivateUserMutation: UseMutationResult<
    unknown,
    ApiError,
    undefined,
    unknown
  >;
  updateUserMutation: UseMutationResult<
    ReadUser,
    ApiError,
    UpdateUser,
    unknown
  >;
  createUserMutation: UseMutationResult<
    ReadUser,
    ApiError,
    CreateUser,
    unknown
  >;
}

export interface QueriesObject {
  userQuery: UseQueryResult<ReadUser, Error>;
}

export const useUserLoading = (
  queries: QueriesObject,
  mutations: MutationsObject,
): UserLoadingState => {
  const { isFetching, isLoading } = queries.userQuery;

  return useMemo((): UserLoadingState => {
    const loadingStates: {
      condition: boolean;
      reason: UserLoadingReason;
    }[] = [
      { condition: isFetching || isLoading, reason: "fetching_user" },
      {
        condition: mutations.activateUserMutation.isPending,
        reason: "activating_user",
      },
      {
        condition: mutations.deactivateUserMutation.isPending,
        reason: "deactivating_user",
      },
      {
        condition: mutations.updateUserMutation.isPending,
        reason: "updating_user",
      },
      {
        condition: mutations.createUserMutation.isPending,
        reason: "creating_user",
      },
    ];

    const activeState = loadingStates.find((state) => state.condition);
    return activeState
      ? { isLoading: true, reason: activeState.reason }
      : { isLoading: false };
  }, [
    isFetching,
    isLoading,
    mutations.activateUserMutation.isPending,
    mutations.deactivateUserMutation.isPending,
    mutations.updateUserMutation.isPending,
    mutations.createUserMutation.isPending,
  ]);
};
